/* eslint-disable max-lines */
import { Badge } from '@heidi/design-system/badge';
import { Chip } from '@heidi/design-system/chip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, getDomainFaviconUrl, getFaviconUrl, useIsMobile } from '@heidi/utils';
import { CheckCircle2, User } from 'lucide-react';
import { useCallback, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import type { EnrichedReference, HtmlEmbedHint } from '../../contracts';
import {
  formatSpecialty,
  getCitationKey,
  getDomainFromUrl,
  getJurisdiction,
  isGuidelineSource,
  isPartnerCitation,
} from '../../contracts';
import { hasLocalRetrieval } from '../../contracts/retrievedFrom';
import { AuthorityTierBadge } from './AuthorityTierBadge';
import { COLLECTIONS_MAX_VISIBLE } from './collectionsDisplay';
import { useCitationDisplay } from './index';
import { IntegrationPartnerBadge } from './IntegrationPartnerBadge';
import { JurisdictionBadge } from './JurisdictionBadge';
import { PartnerLogo } from './PartnerLogo';
import { useEvidenceAnswerPorts } from './ports';
import { RetrievedFromLabel } from './RetrievedFromLabel';
import { scrollToInlineCitation } from './scrollToCitation';
import {
  SOURCE_ICON_PROPS,
  getFileSourceIcon,
  getFileTypeLabel,
  getSourceTypeLabel,
} from './sourceIconUtils';
import { shouldOpenSourceInViewer } from './sourceViewerRouting';

// ============================================================================
// Constants
// ============================================================================

const ICON_SIZE = SOURCE_ICON_PROPS.size;

// ============================================================================
// Inline Components
// ============================================================================
//
// Meta-row convention: the four inner row components below
// (`UserDocumentMetaRow`, `CollectionsRow`, `PartnerMetaRow`,
// `MetadataBadgeRow`) carry `col-start-1` on their own root rather than on a
// call-site wrapper. The parent `ReferenceContentGrid` uses
// `grid-cols-[1fr_auto]`, and a wrapper around a component that returns `null`
// would still mount as an empty grid cell — adding a phantom row track + the
// surrounding `gap-y-1`. Self-placing at `col-start-1` lets a meta-less render
// mount NOTHING. The `col-start-1` is a no-op in the compact path's
// `grid-cols-1`.
// ============================================================================

/** Icon + text pair with consistent spacing */
const MetaItem = ({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <span className="inline-flex items-center gap-1">
    {icon}
    {children}
  </span>
);

/** Favicon with fallback */
const Favicon = ({ url }: { url: string }) => {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={getFaviconUrl(url)}
      alt=""
      className="size-3.5 rounded-sm"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

/** File icon for file-based sources using shared utility */
const FileIconBadge = ({
  fileName,
  sourceType,
}: {
  fileName: string;
  sourceType?: string;
}) => {
  const FileIcon = getFileSourceIcon(sourceType, fileName);
  if (!FileIcon) return null;
  return <FileIcon size={14} className="text-text-secondary" />;
};

/** Source name with favicon or file icon */
const SourceBadge = ({
  url,
  name,
  isFileSource,
  fileName,
  sourceType,
}: {
  url?: string;
  name: string;
  isFileSource?: boolean;
  fileName?: string;
  sourceType?: string;
}) => (
  <MetaItem
    icon={
      isFileSource && fileName ? (
        <FileIconBadge fileName={fileName} sourceType={sourceType} />
      ) : (
        <Favicon url={url || ''} />
      )
    }
  >
    {name}
  </MetaItem>
);

/**
 * EVI-678: Partner source badge with icon and branded name.
 * Shows partner logo (placeholder) and name with brand color.
 */
const PartnerSourceBadge = ({
  partnerType,
  sourceUrl,
  logoUrl,
  brandColor,
}: {
  partnerType: string;
  sourceUrl?: string;
  // EVI-1105: per-citation branding overrides (zone-specific HealthPathways logo / colour)
  logoUrl?: string;
  brandColor?: string;
}) => {
  const { getPartnerConfig } = useEvidenceAnswerPorts();
  const partnerConfig = getPartnerConfig(partnerType);
  if (!partnerConfig) return null;

  // EVI-1105: suppress the sibling name only when rendering a per-citation zone
  // logo (HealthPathways CHP/HHP wordmark). The neutral partner logo keeps its label.
  const hideName = !!logoUrl;
  // Prefer per-citation branding, fall back to the partner-level catalog.
  const effectiveLogo = logoUrl ?? partnerConfig.logo;
  const effectiveBrandColor = brandColor ?? partnerConfig.brandColor;

  return (
    <MetaItem
      icon={
        <PartnerLogo
          src={effectiveLogo}
          brandColor={effectiveBrandColor}
          size={hideName ? 18 : 14}
          alt={partnerConfig.displayName}
          sourceUrl={sourceUrl}
          wordmark={hideName}
        />
      }
    >
      {hideName ? null : (
        <span style={{ color: effectiveBrandColor }}>{partnerConfig.displayName}</span>
      )}
    </MetaItem>
  );
};

/** Website type badge — text label (e.g. "Journal", "Medical Society") */
const TypeBadge = ({ type }: { type: string }) => {
  const label = getSourceTypeLabel(type);
  return (
    <Badge
      color="neutral"
      size="sm"
      className="bg-transparent font-normal text-text-secondary"
      data-testid="reference-source-type"
    >
      {label}
    </Badge>
  );
};

/**
 * Impact factor badge for journals — transparent overrides for clean appearance.
 */
const ImpactFactorBadge = ({ factor }: { factor: number }) => {
  const { t } = useTranslations();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Badge
            color="neutral"
            size="sm"
            data-testid="reference-impact-factor"
            className="bg-background-tertiary font-normal"
          >
            {t('chat.impactFactor', { value: factor.toFixed(1) })}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {t('chat.journalImpactFactor')}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * EVI-605: User document metadata row.
 * Displays author and created date for library documents.
 */
const UserDocumentMetaRow = ({ reference }: { reference: EnrichedReference }) => {
  const hasMetadata = reference.document_author || reference.document_created_at;

  if (!hasMetadata) return null;

  // Format date if present
  const formattedDate = reference.document_created_at
    ? new Date(reference.document_created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div
      className={cn(
        typographyVariant({ variant: 'caption' }),
        'col-start-1 flex min-w-0 flex-wrap items-center gap-x-2 text-text-tertiary'
      )}
    >
      {reference.document_author && (
        <MetaItem icon={<User size={ICON_SIZE} />}>{reference.document_author}</MetaItem>
      )}
      {formattedDate && <span>{formattedDate}</span>}
    </div>
  );
};

/**
 * Collection provenance row for user documents and web collection domains.
 *
 * Renders each collection as a `purple` Chip from the design system. The
 * entity color is deliberately distinct from the `neutral` "In region"
 * jurisdiction badge and the skies-blue authority/guideline badges so a
 * collection chip never reads as a relevance/authority signal. Wraps to
 * multiple lines via `flex-wrap`. If there are more than
 * `COLLECTIONS_MAX_VISIBLE` collections, the surplus is collapsed into a
 * `+N more` chip with a tooltip enumerating the hidden names.
 */
const CollectionsRow = ({ reference }: { reference: EnrichedReference }) => {
  const intl = useIntl();
  const collections = reference.collections;
  if (!collections?.length) return null;

  const label = intl.formatMessage(
    {
      defaultMessage: '{count, plural, one {Collection:} other {Collections:}}',
      id: 'JX8NGpEbAo',
    },
    { count: collections.length }
  );
  const visible = collections.slice(0, COLLECTIONS_MAX_VISIBLE);
  const hidden = collections.slice(COLLECTIONS_MAX_VISIBLE);

  return (
    <div className="col-start-1 mt-1 flex min-w-0 flex-wrap items-center gap-1 pl-1.5">
      <span className={cn(typographyVariant({ variant: 'p2' }), 'text-text-tertiary')}>
        {label}
      </span>
      {visible.map((name, idx) => (
        <Chip key={`${name}-${idx}`} color="purple">
          {name}
        </Chip>
      ))}
      {/*
       * Bare Tooltip (not a nested TooltipProvider): ReferenceList already
       * supplies a single ancestor provider with delayDuration=300, so the
       * `+N more` hover-delay stays consistent with the sibling badges.
       */}
      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Chip color="purple" variant="ghost" tabIndex={0}>
              {intl.formatMessage(
                { defaultMessage: '+{count} more', id: '/zFGgPmQSV' },
                { count: hidden.length }
              )}
            </Chip>
          </TooltipTrigger>
          <TooltipContent>{hidden.join(', ')}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

/**
 * EVI-678: Integration partner metadata row.
 * Displays content type, author, and last updated date for partner sources.
 * Uses generic fields (content_type, author, pageAge) - same fields usable by other source types.
 */
const PartnerMetaRow = ({
  reference,
  brandColor,
}: {
  reference: EnrichedReference;
  brandColor?: string;
}) => {
  // Use generic fields - content_type, author, and pageAge (reused for "last updated")
  const hasMetadata = reference.content_type || reference.author || reference.pageAge;

  if (!hasMetadata) return null;

  return (
    <div
      className={cn(
        typographyVariant({ variant: 'caption' }),
        'col-start-1 flex min-w-0 flex-wrap items-center gap-x-2 text-text-tertiary'
      )}
    >
      {reference.content_type && (
        <Badge
          color="inverted"
          size="sm"
          className="font-normal"
          style={
            brandColor
              ? { backgroundColor: `${brandColor}15`, color: brandColor }
              : undefined
          }
        >
          {reference.content_type}
        </Badge>
      )}
      {reference.author && (
        <MetaItem icon={<User size={ICON_SIZE} />}>{reference.author}</MetaItem>
      )}
      {reference.pageAge && <span>{reference.pageAge}</span>}
    </div>
  );
};

/** Clickable title that opens source in viewer (desktop) or new tab (mobile) */
const TitleLink = ({
  id,
  title,
  url,
  onTrack,
  domain,
  sourceName,
  iconUrl,
  embed,
}: {
  /** Stable source identifier (e.g., `integration:mims:...`); preferred over URL for CPD tracking */
  id?: string;
  title: string;
  url: string;
  onTrack: () => void;
  domain?: string;
  sourceName?: string;
  iconUrl?: string;
  /** BE embeddability hint — `{ mode: 'html' }` forces inline rendering via the proxy */
  embed?: HtmlEmbedHint;
}) => {
  const { openInViewer, openInNewTab, isAuthenticatedUrl, markSourceRead } =
    useEvidenceAnswerPorts();
  const isMobile = useIsMobile();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTrack();

    if (shouldOpenSourceInViewer({ url, embed, isMobile, isAuthenticatedUrl })) {
      openInViewer({
        id,
        url,
        title,
        domain:
          domain ||
          (() => {
            try {
              return new URL(url).hostname;
            } catch {
              return '';
            }
          })(),
        sourceName,
        iconUrl,
        embed,
      });
    } else {
      openInNewTab(url);
    }

    // Skip URL-keyed click-time marking when we have a stable id — the sheet's
    // close handler will call markSourceAsRead(id, elapsed) with the real read
    // time. URL-keyed marking is only needed when no id is available, since
    // presigned URLs (e.g. MIMS) rotate per fetch and would create orphan stubs.
    if (!id) {
      markSourceRead(url, title);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        typographyVariant({ variant: 'p2Bold' }),
        'min-w-0 text-start text-text-primary hover:underline'
      )}
    >
      <span className="break-words">{title}</span>
    </button>
  );
};

/** Non-clickable title for citations without URL */
const TitleText = ({ title }: { title: string }) => (
  <span
    className={cn(
      typographyVariant({ variant: 'p2Bold' }),
      'min-w-0 text-start text-text-primary'
    )}
  >
    <span className="break-words">{title}</span>
  </span>
);

/**
 * Clickable title for library-document references — opens the in-app
 * document preview (via `documentPreviewAtom`) and emits
 * `evidence_source_clicked` with `interaction: 'open_library_document'`.
 *
 * UX parity: library document references behave like web ones — title is the
 * link to the source content (here: the PDF preview), while the surrounding
 * row click is reserved for "scroll to the inline citation". This handler
 * `stopPropagation`s so a title click does NOT trigger the row's scroll.
 */
const TitleLibraryDocLink = ({
  title,
  documentId,
  onTrack,
}: {
  title: string;
  documentId: string;
  onTrack: () => void;
}) => {
  const { openLibraryDocument } = useEvidenceAnswerPorts();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTrack();
    openLibraryDocument(documentId);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        typographyVariant({ variant: 'p2Bold' }),
        'min-w-0 text-start text-text-primary hover:underline'
      )}
    >
      <span className="break-words">{title}</span>
    </button>
  );
};

/**
 * Source identity row: favicon/icon + name + file type label.
 * Separated from metadata badges for a cleaner two-layer layout.
 */
const SourceIdentityRow = ({
  reference,
  fullDisplayName,
  isFileSource,
  sourceLabel,
  isStreaming = false,
  hasLocalSources = false,
}: {
  reference: EnrichedReference;
  fullDisplayName: string;
  isFileSource: boolean;
  sourceLabel: string | null;
  /** MOD-1726: the answer cites a locally retrieved source — see `ReferenceList`. */
  hasLocalSources?: boolean;
  /** While the response is still streaming, suppress the enrichment badge
   *  group — it lands asynchronously and would reflow the row (EVI bump). */
  isStreaming?: boolean;
}) => {
  const { t } = useTranslations();
  const { ocrQualityTag: OcrQualityTag } = useEvidenceAnswerPorts();
  const isPartnerSource = isPartnerCitation(reference.source_type);
  const authorityScore = reference.authoritativeness_score;
  const hasAuthority = !isPartnerSource && authorityScore != null;
  const showGuideline = !isPartnerSource && isGuidelineSource(reference.websiteType);
  // Jurisdiction labelling (EV-022): regional / global / outOfRegion. The
  // BE owns the classification and emits `regional_relevance` only when it
  // could resolve the user's country, so its absence means "no chip".
  const jurisdiction = !isPartnerSource
    ? getJurisdiction(reference.regional_relevance)
    : null;

  return (
    <div
      className={cn(
        typographyVariant({ variant: 'p2' }),
        'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-text-tertiary'
      )}
    >
      {/* Partner sources get branded badge */}
      {isPartnerSource && reference.partner_type ? (
        <PartnerSourceBadge
          partnerType={reference.partner_type}
          sourceUrl={reference.url}
          logoUrl={reference.logo_url}
          brandColor={reference.brand_color}
        />
      ) : (
        <SourceBadge
          url={reference.url}
          name={isFileSource && sourceLabel ? sourceLabel : fullDisplayName}
          isFileSource={isFileSource}
          fileName={reference.file_name}
          sourceType={reference.source_type}
        />
      )}
      {/* Integration Partner indicator chip */}
      {isPartnerSource && <IntegrationPartnerBadge />}
      {/* Dot divider + Date — inline after publisher name */}
      {!isFileSource && reference.pageAge && (
        <>
          <span aria-hidden="true">·</span>
          <span>{reference.pageAge}</span>
        </>
      )}
      {/* MOD-1726: where an on-prem appliance retrieved the source from */}
      <RetrievedFromLabel
        retrievedFrom={reference.retrieved_from}
        hasLocalSources={hasLocalSources}
        withDivider
      />
      {/* Badges group — extra left spacing to separate from text metadata.
          Held back while streaming so the row keeps a stable height (the
          authority/guideline/jurisdiction badges enrich in asynchronously). */}
      {!isStreaming && (showGuideline || hasAuthority || jurisdiction) && (
        <span className="ml-1 inline-flex flex-wrap items-center gap-1.5">
          {/* Guideline badge */}
          {showGuideline && (
            <Badge color="blue" size="sm">
              <CheckCircle2 />
              {t('chat.guideline')}
            </Badge>
          )}
          {/* Authority tier badge — hover shows metadata details */}
          {!isPartnerSource && authorityScore != null && (
            <AuthorityTierBadge score={authorityScore} size="sm" reference={reference} />
          )}
          {/* Jurisdiction badge — regional / international / foreign (EV-022) */}
          {jurisdiction && <JurisdictionBadge jurisdiction={jurisdiction} />}
        </span>
      )}
      {/* File type label for file-based sources (e.g., "PDF", "DOCX") */}
      {isFileSource && getFileTypeLabel(reference.file_name) && (
        <TypographyCaption>({getFileTypeLabel(reference.file_name)})</TypographyCaption>
      )}
      {/* EVI-1566: text-extraction quality warning for cited library documents */}
      <OcrQualityTag citation={reference} />
    </div>
  );
};

/**
 * Metadata badge row: authority tier, guideline, local, website type, content type,
 * impact factor, specialty. Only renders for web sources that have metadata.
 */
const MetadataBadgeRow = ({ reference }: { reference: EnrichedReference }) => {
  const isPartnerSource = isPartnerCitation(reference.source_type);
  // Guideline detection — used for content_type deduplication
  const showGuideline = !isPartnerSource && isGuidelineSource(reference.websiteType);

  // Content type — suppress if it duplicates the guideline badge
  const showContentType =
    !!reference.content_type &&
    !(showGuideline && reference.content_type.toLowerCase().includes('guideline'));

  // Collect whether we have any badges to show. Mirror the render-side
  // `!isPartnerSource` gates exactly — otherwise a partner ref with a populated
  // `websiteType` / `impact_factor` / `specialty` passes the early return,
  // every inner badge is suppressed, and we mount an empty `<div>` that
  // shows up as a phantom row track in the parent grid.
  const hasBadges =
    (!isPartnerSource && reference.websiteType?.[0]) ||
    showContentType ||
    (!isPartnerSource && reference.impact_factor !== undefined) ||
    (!isPartnerSource && reference.specialty?.[0]);

  if (!hasBadges) return null;

  return (
    <div className="col-start-1 mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
      {/* Website type as text label (e.g. "Journal", "Medical Society") */}
      {!isPartnerSource && reference.websiteType?.[0] && (
        <TypeBadge type={reference.websiteType[0]} />
      )}
      {/* Content type badge for all source types */}
      {showContentType && (
        <Badge
          color="neutral"
          size="sm"
          className="bg-transparent font-normal text-text-secondary"
        >
          {reference.content_type}
        </Badge>
      )}
      {/* Impact factor for journals */}
      {!isPartnerSource && reference.impact_factor !== undefined && (
        <ImpactFactorBadge factor={reference.impact_factor} />
      )}
      {/* Specialty tag as badge — show first specialty only */}
      {!isPartnerSource && reference.specialty?.[0] && (
        <Badge
          color="neutral"
          size="sm"
          className="bg-transparent font-normal text-text-secondary"
        >
          {formatSpecialty(reference.specialty[0])}
        </Badge>
      )}
    </div>
  );
};

interface ReferenceContentGridProps {
  reference: EnrichedReference;
  index: number;
  chatId?: string;
  sessionId?: string;
  messageId?: string;
  compact?: boolean;
  /** While the response streams, hold back the reflow-prone enrichment
   *  sub-rows so reference rows keep a stable height (EVI bump fix). */
  isStreaming?: boolean;
  hasLocalSources?: boolean;
}

/**
 * Shared content grid for reference rows.
 * Used by both partner and non-partner variants to avoid duplication.
 * Computes display properties internally from the reference object.
 */
const ReferenceContentGrid = ({
  reference,
  index,
  chatId,
  sessionId,
  messageId,
  compact = false,
  isStreaming = false,
  hasLocalSources = false,
}: ReferenceContentGridProps) => {
  // Derive display properties from reference
  const { displayName, fullDisplayName, isFileSource, sourceLabel, isClickable } =
    useCitationDisplay(reference);
  const {
    trackSourceClick,
    trackAnswerEvent,
    getPartnerConfig,
    sourceActionsComponent: SourceCardActions,
  } = useEvidenceAnswerPorts();
  const isPartnerSource = isPartnerCitation(reference.source_type);
  const partnerConfig =
    isPartnerSource && reference.partner_type
      ? getPartnerConfig(reference.partner_type)
      : undefined;
  const titleToShow = reference.title || displayName;

  const isMobile = useIsMobile();

  // EVI-1486: library docs open the in-app PDF preview ('open_library_document')
  // rather than the web viewer / new tab. Derived once so the callback below
  // has a stable interaction value.
  const isLibraryDoc = reference.source_type === 'user_document';
  const interaction: 'open_library_document' | 'open_in_new_tab' | 'open_in_viewer' =
    isLibraryDoc
      ? 'open_library_document'
      : isMobile
        ? 'open_in_new_tab'
        : 'open_in_viewer';

  const trackClick = useCallback(() => {
    // EVI-1486: unified `evidence_source_clicked` shape — adds `surface`,
    // `interaction`, `chat_id`, `message_id`, `session_id`, `source_type`,
    // and `reference_index` so dashboards can answer "who clicked which
    // source from which surface" without joining multiple events.
    trackSourceClick({
      source_url: reference.url || '',
      source_domain: reference.domain,
      // Omit library document titles — filenames often include patient identifiers.
      source_title: isLibraryDoc ? undefined : reference.title || undefined,
      source_type: reference.source_type,
      // EVI-1486 / EVI-1592: collections are org library labels (product analytics).
      // Omit free-form names from Snowplow until PHI policy confirms they are safe.
      collections: undefined,
      surface: 'references_section',
      interaction,
      chat_id: chatId,
      message_id: messageId,
      session_id: sessionId,
      reference_index: index,
    });
    // Generic object_action event — kept in lockstep for the existing
    // button-clicked dashboard.
    trackAnswerEvent({
      type: 'source_selected',
      source_url: reference.url || '',
      source_domain: reference.domain,
      source_title: isLibraryDoc ? undefined : reference.title || undefined,
    });
  }, [
    reference,
    chatId,
    messageId,
    sessionId,
    index,
    interaction,
    isLibraryDoc,
    trackSourceClick,
    trackAnswerEvent,
  ]);

  // EVI-1486: library-document references should behave like web references —
  // title click opens the source content (the in-app preview), row click
  // scrolls to the inline citation. Previously `isClickable = !!url` was
  // false for user_document (no URL), so the title rendered as plain text
  // and the only way to "open" the doc was via the row-scroll.
  const libraryDocumentId =
    reference.source_type === 'user_document'
      ? reference.source_id || reference.document_id
      : undefined;

  let titleElement: JSX.Element;
  if (libraryDocumentId) {
    titleElement = (
      <TitleLibraryDocLink
        title={titleToShow}
        documentId={libraryDocumentId}
        onTrack={trackClick}
      />
    );
  } else if (isClickable && reference.url) {
    // Fall back to URL hostname when `reference.domain` is missing so the
    // favicon URL never embeds the literal string "undefined" — matches the
    // resolution chain used by `buildViewerSource`.
    const displayDomain = reference.domain || getDomainFromUrl(reference.url) || '';
    titleElement = (
      <TitleLink
        id={reference.source_id}
        title={titleToShow}
        url={reference.url}
        onTrack={trackClick}
        domain={displayDomain}
        sourceName={fullDisplayName}
        iconUrl={displayDomain ? getDomainFaviconUrl(displayDomain) : undefined}
        embed={reference.embed}
      />
    );
  } else {
    titleElement = <TitleText title={titleToShow} />;
  }

  const actionsElement = (
    <SourceCardActions
      source={reference}
      index={index}
      chatId={chatId}
      sessionId={sessionId}
      messageId={messageId}
      size="xxs"
      noHoverBg={compact}
      surface="references_section"
    />
  );

  const sourceIdentityElement = (
    <SourceIdentityRow
      reference={reference}
      fullDisplayName={fullDisplayName}
      isFileSource={isFileSource}
      sourceLabel={sourceLabel}
      isStreaming={isStreaming}
      hasLocalSources={hasLocalSources}
    />
  );

  // In-session (compact): single-column grid with overlay action buttons on hover.
  // Actions float over the title with a gradient mask so the title gets full width.
  if (compact) {
    return (
      <div className="relative grid w-full min-w-0 grid-cols-1 gap-y-1">
        {/* Row 1: Title (full width) */}
        {titleElement}
        {/* Row 2: Source identity */}
        {sourceIdentityElement}
        {/* User document metadata row */}
        {isFileSource && reference.source_type === 'user_document' && (
          <UserDocumentMetaRow reference={reference} />
        )}
        {/* Collection provenance row */}
        {reference.collections?.length ? <CollectionsRow reference={reference} /> : null}
        {/* Partner metadata row */}
        {isPartnerSource && (
          <PartnerMetaRow reference={reference} brandColor={partnerConfig?.brandColor} />
        )}
        {/* Hover overlay: anchored bottom inline-end, aligned with badge row */}
        <div
          className={cn(
            'absolute bottom-0 end-0 flex items-end',
            'pointer-events-none opacity-0',
            'ease transition-opacity duration-200',
            'group-hover:pointer-events-auto group-hover:opacity-100'
          )}
        >
          {/* Gradient mask that blends into the hover background; the gradient
              direction is physical, so mirror it in RTL to fade toward content */}
          <div className="w-6 self-stretch bg-gradient-to-r from-transparent to-background-tertiary rtl:bg-gradient-to-l" />
          {/* Solid background behind buttons */}
          <div className="flex items-center bg-background-tertiary pe-1">
            {actionsElement}
          </div>
        </div>
      </div>
    );
  }

  // Out-of-session: two-column grid. Row 1 spans both columns (title + hover
  // actions); every subsequent row self-places at `col-start-1` (see meta-row
  // convention in the Inline Components header).
  return (
    <div className="grid w-full min-w-0 grid-cols-[1fr_auto] gap-x-4 gap-y-1">
      {/* Row 1: Title | Actions (top-aligned, shown on hover) */}
      {titleElement}
      <div className="ease self-start opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        {actionsElement}
      </div>
      {/* Row 2: Source identity (favicon + name + file type) */}
      <div className="col-start-1 min-w-0">{sourceIdentityElement}</div>
      {/* Row 3: Metadata badges. Held back while streaming — these enrich
          asynchronously and add a whole line, the main source of the
          reference-row "bump" as a response streams (EVI). */}
      {!isStreaming && !isFileSource && <MetadataBadgeRow reference={reference} />}
      {/* User document metadata row */}
      {isFileSource && reference.source_type === 'user_document' && (
        <UserDocumentMetaRow reference={reference} />
      )}
      {/* Collection provenance row */}
      {reference.collections?.length ? <CollectionsRow reference={reference} /> : null}
      {/* Partner metadata row */}
      {isPartnerSource && (
        <PartnerMetaRow reference={reference} brandColor={partnerConfig?.brandColor} />
      )}
    </div>
  );
};

// ============================================================================
// Main Components
// ============================================================================

interface ReferenceCardProps {
  reference: EnrichedReference;
  index: number;
  chatId?: string;
  sessionId?: string;
  messageId?: string;
  compact?: boolean;
  /** Forwarded to the content grid to stabilise row height while streaming. */
  isStreaming?: boolean;
  /** MOD-1726: the answer cites a local source, so a `web` row is labelled too. */
  hasLocalSources?: boolean;
}

/** Single reference row — an `<li>` in the parent `ReferenceList`'s `<ul>`. */
export const ReferenceCard = ({
  reference,
  index,
  chatId,
  sessionId,
  messageId,
  compact = false,
  isStreaming = false,
  hasLocalSources = false,
}: ReferenceCardProps) => {
  const { getPartnerConfig, trackAnswerEvent } = useEvidenceAnswerPorts();

  /**
   * EVI-385: Scroll to the first inline citation matching this reference.
   * Clicking the reference number scrolls the user to where it's cited in the response.
   * EVI-383: Track the scroll interaction for analytics.
   */
  const citationIdentifier = getCitationKey(reference);

  const handleScrollToCitation = useCallback(() => {
    const scrollSucceeded = scrollToInlineCitation(citationIdentifier);
    trackAnswerEvent({
      type: 'reference_scroll_clicked',
      reference_index: index,
      source_url: reference.url || '',
      source_domain: reference.domain,
      scroll_succeeded: scrollSucceeded,
      chat_id: chatId,
      // EVI-1486 payload completeness — same join keys and classification
      // fields as `evidence_source_clicked` so dashboards can union the
      // two events without bespoke joins.
      message_id: messageId,
      source_type: reference.source_type,
      // Omit library document titles — filenames often include patient identifiers.
      source_title:
        reference.source_type === 'user_document'
          ? undefined
          : reference.title || undefined,
      // Omit free-form collection labels until PHI policy confirms they are safe.
      collections: undefined,
    });
  }, [
    citationIdentifier,
    reference.url,
    reference.domain,
    reference.source_type,
    reference.title,
    index,
    chatId,
    messageId,
    trackAnswerEvent,
  ]);

  // Every ref renders its index inside the same DS `Badge` chip so the column
  // reads consistently. Integration partner refs override the chip's
  // background + foreground with the partner brand colour via inline `style`
  // (the digit's tint is the partner signal); non-partner refs fall back to
  // the neutral palette. `0d` ≈ 5% opacity over the brand colour.
  const isPartnerSource = isPartnerCitation(reference.source_type);
  const partnerConfig =
    isPartnerSource && reference.partner_type
      ? getPartnerConfig(reference.partner_type)
      : undefined;
  const partnerBrandStyle: CSSProperties | undefined = partnerConfig?.brandColor
    ? {
        backgroundColor: `${partnerConfig.brandColor}0d`,
        color: partnerConfig.brandColor,
      }
    : undefined;

  return (
    <li>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- behaviour parity with the previous <tr onClick> row. The row-level "click anywhere to scroll to the citation" affordance is mouse-only by design; promoting this wrapper to a <button> (or adding `role="button"` + onKeyDown) would nest the action/title <button>s rendered by ReferenceContentGrid inside an interactive ancestor, which is the worse a11y violation. Keyboard users get to the same destinations via the title link and per-action buttons, which ARE focusable. Revisit if the row gains a keyboard-only-reachable behaviour. */}
      <div
        data-testid="reference-row"
        onClick={handleScrollToCitation}
        className="group flex cursor-pointer items-baseline gap-2.5 rounded-lg px-2.5 pb-2.5 pt-2 transition-colors hover:bg-background-tertiary"
      >
        <div className="relative -top-px shrink-0">
          <Badge
            color="neutral"
            size="sm"
            className="min-w-5 justify-center"
            style={partnerBrandStyle}
          >
            {index + 1}
          </Badge>
        </div>

        <div className="min-w-0 flex-1">
          <ReferenceContentGrid
            reference={reference}
            index={index}
            chatId={chatId}
            sessionId={sessionId}
            messageId={messageId}
            compact={compact}
            isStreaming={isStreaming}
            hasLocalSources={hasLocalSources}
          />
        </div>
      </div>
    </li>
  );
};

interface ReferenceListProps {
  references: EnrichedReference[];
  chatId?: string;
  sessionId?: string;
  messageId?: string;
  compact?: boolean;
  /** While true, rows suppress async-enriched badges so heights stay stable. */
  isStreaming?: boolean;
}

/** List container for reference cards. Single TooltipProvider for every nested badge tooltip. */
export const ReferenceList = ({
  references,
  chatId,
  sessionId,
  messageId,
  compact = false,
  isStreaming = false,
}: ReferenceListProps) => {
  // MOD-1726: `references` is the whole answer's source list, so mixed-ness is
  // decided here once and handed to every row.
  const hasLocalSources = hasLocalRetrieval(references);

  return (
    <TooltipProvider delayDuration={300}>
      <ul data-testid="reference-list" className="flex min-w-0 flex-col gap-2 pt-2">
        {references.map((ref, i) => (
          <ReferenceCard
            key={`${i}-${ref.url}`}
            reference={ref}
            index={i}
            chatId={chatId}
            sessionId={sessionId}
            messageId={messageId}
            compact={compact}
            isStreaming={isStreaming}
            hasLocalSources={hasLocalSources}
          />
        ))}
      </ul>
    </TooltipProvider>
  );
};
